import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { Box, Button, Card, IconButton, Paper, Skeleton, Typography } from "@mui/material";
import { useState } from "react";
import { GradientStar } from "../../../styles/icons/gradient-star";
import { useDialog } from "../../../utils/hooks/use-dialog";
import { AdditionalServicesModal } from "../../additional-services/additional-services-modal";
import { useAdditionalServiceItems } from "../../additional-services/use-additional-service-items";
import { SeverityPill } from "../../generic/components/severity-pill";

import type { AdditionalServicesType } from "../../additional-services/type";
import type { AdditionalServiceItem } from "../../additional-services/use-additional-service-items";
import type { FC } from "react";

// Room for any item's title, so the tile keeps its height on load and between items.
const TILE_HEIGHT = 200;

/** New initiatives and features to highlight: the home page's "Do something special" items. */
export const WhatsNewCard: FC = () => {
  const dialog = useDialog<{ service: AdditionalServicesType }>();
  const items = useAdditionalServiceItems();
  const [index, setIndex] = useState(0);

  if (items?.length === 0) return null;

  const shown = items?.[index % items.length];

  return (
    <Card
      sx={{
        p: 2,
        height: "100%",
        boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 34,
          mb: 2,
        }}
      >
        <Paper
          elevation={2}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.75,
            "& svg": { width: 18, height: 18 },
          }}
        >
          <GradientStar />
          <Typography variant="subtitle2">What’s new?</Typography>
        </Paper>
        {items && items.length > 1 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              size="small"
              color="primary"
              aria-label="Previous"
              data-analytics-id="dashboard-whats-new-previous"
              onClick={() => setIndex((index + items.length - 1) % items.length)}
            >
              <ChevronLeft />
            </IconButton>
            <Typography
              variant="subtitle2"
              // Digits of one width, so the arrows don't shift as the count changes.
              sx={{ fontVariantNumeric: "tabular-nums", minWidth: "6ch", textAlign: "center" }}
            >
              {(index % items.length) + 1} / {items.length}
            </Typography>
            <IconButton
              size="small"
              color="primary"
              aria-label="Next"
              data-analytics-id="dashboard-whats-new-next"
              onClick={() => setIndex((index + 1) % items.length)}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        )}
      </Box>

      {items ? (
        <ItemTiles
          items={items}
          shown={shown}
          onOpen={(service) => dialog.handleOpen({ service })}
        />
      ) : (
        <Skeleton variant="rounded" height={TILE_HEIGHT} />
      )}
      <AdditionalServicesModal dialog={dialog} />
    </Card>
  );
};

/**
 * Every item sits in the same grid cell, with only the shown one visible, so the tile is as tall
 * as its tallest item and stays put while stepping through them.
 */
const ItemTiles: FC<{
  items: AdditionalServiceItem[];
  shown: AdditionalServiceItem | undefined;
  onOpen: (service: AdditionalServicesType) => void;
}> = ({ items, shown, onOpen }) => (
  <Box sx={{ display: "grid" }}>
    {items.map((item) => {
      const current = item === shown;
      return (
        <Box
          key={item.key}
          aria-hidden={!current}
          inert={!current}
          sx={[
            {
              gridArea: "1 / 1",
              visibility: current ? "visible" : "hidden",
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              minHeight: TILE_HEIGHT,
              borderRadius: 2,
            },
            (theme) => ({
              bgcolor: theme.palette.mode === "dark" ? theme.palette.grey[900] : "#FED234",
            }),
          ]}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              alignSelf: "stretch",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <SeverityPill color="primary">{item.name}</SeverityPill>
            <Typography variant="body2" sx={{ my: 2 }}>
              {item.title}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => onOpen(item.service)}
              data-analytics-id={`dashboard-whats-new-${item.key}`}
            >
              {item.description}
            </Button>
          </Box>
          <Box
            component="img"
            src={item.image}
            alt=""
            sx={{ width: 187, height: 140, objectFit: "contain" }}
          />
        </Box>
      );
    })}
  </Box>
);
